import React, { useState } from 'react';
import { X, MessageCircle } from 'lucide-react';

const styles = {
  container: {
    backgroundColor: '#0F172A',
    color: '#E2E8F0',
    fontFamily: 'Cairo, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    minHeight: '100vh',
    padding: '20px',
    backgroundImage: 'radial-gradient(circle at 50% 50%, #1E293B 0%, #0F172A 100%)',
  },
  header: {
    textAlign: 'center',
    padding: '50px 0',
  },
  heading: {
    fontSize: '3.5em',
    margin: '0',
    color: '#60A5FA',
    fontWeight: '200',
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
  },
  section: {
    margin: '50px 0',
  },
  embedContainer: {
    width: '100%',
    height: '600px',
    marginBottom: '30px',
    borderRadius: '16px',
    overflow: 'hidden',
    border: '1px solid rgba(96, 165, 250, 0.1)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '24px',
    marginTop: '20px',
  },
  card: {
    backgroundColor: 'rgba(45, 55, 72, 0.3)',
    borderRadius: '16px',
    padding: '24px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    border: '1px solid rgba(96, 165, 250, 0.05)',
    display: 'block',
    textDecoration: 'none',
    color: 'inherit',
    backdropFilter: 'blur(10px)',
  },
  cardHover: {
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 25px rgba(96, 165, 250, 0.1)',
    border: '1px solid rgba(96, 165, 250, 0.2)',
    backgroundColor: 'rgba(45, 55, 72, 0.4)',
  },
  cardIcon: {
    fontSize: '3em',
  },
  footer: {
    textAlign: 'center',
    padding: '40px 20px',
    backgroundColor: 'rgba(45, 55, 72, 0.2)',
    marginTop: '40px',
    borderTop: '1px solid rgba(96, 165, 250, 0.05)',
    borderRadius: '0 0 16px 16px',
    backdropFilter: 'blur(10px)',
  },
  link: {
    color: '#60A5FA',
    textDecoration: 'none',
    transition: 'color 0.2s ease',
    padding: '0 15px',
    fontWeight: '300',
    letterSpacing: '0.05em',
    fontSize: '0.95em',
    textTransform: 'uppercase',
  },
  disclaimer: {
    fontSize: '0.9em',
    color: '#94A3B8',
    marginTop: '20px',
    maxWidth: '800px',
    margin: '20px auto 0',
    lineHeight: '1.8',
    fontWeight: '300',
    letterSpacing: '0.02em',
  },
  chatButton: {
    position: 'fixed',
    bottom: '2rem',
    right: '2rem',
    backgroundColor: '#60A5FA',
    borderRadius: '50%',
    width: '3.5rem',
    height: '3.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    border: 'none',
    boxShadow: '0 4px 12px rgba(96, 165, 250, 0.3)',
  },
  chatPopup: {
    position: 'fixed',
    bottom: '6rem',
    right: '2rem',
    width: '360px',
    height: '480px',
    backgroundColor: '#1E293B',
    borderRadius: '16px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
    border: '1px solid rgba(96, 165, 250, 0.1)',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
  },
  chatHeader: {
    backgroundColor: '#60A5FA',
    padding: '1rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
};

const Card = ({ href, icon }) => {
  const [isHovered, setIsHovered] = React.useState(false);

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        ...styles.card,
        ...(isHovered ? styles.cardHover : {}),
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div style={styles.cardIcon}>{icon}</div>
    </a>
  );
};

const ChatPopup = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div style={styles.chatPopup}>
      <div style={styles.chatHeader}>
        <h3 style={{ margin: 0, color: 'white' }}>Contact Us</h3>
        <button 
          style={styles.closeButton} 
          onClick={onClose}
          aria-label="Close chat"
        >
          <X color="white" size={20} />
        </button>
      </div>
      <iframe
        src="https://investor-relations-chatbot.zapier.app"
        style={{ 
          width: '100%', 
          height: 'calc(100% - 56px)', 
          border: 'none' 
        }}
        title="Contact Chat"
      />
    </div>
  );
};

const App = () => {
  const [isChatOpen, setIsChatOpen] = useState(false);

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <img
          src="/nerdlecoin.png"
          alt="NerdleCoin Logo"
          style={{ 
            width: '300px', 
            height: '300px',
            marginBottom: '20px',
            filter: 'drop-shadow(0 0 20px rgba(96, 165, 250, 0.3))'
          }}
        />
        <h1 style={styles.heading}>NerdleCoin</h1>
        <p style={{ 
          color: '#60A5FA', 
          marginBottom: '20px',
          fontSize: '1.25em',
          fontWeight: '300',
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
        }}>
          🚀 The World's First Interplanetary Currency
        </p>
        <p style={{ 
          color: '#E2E8F0', 
          maxWidth: '800px', 
          margin: '0 auto',
          lineHeight: '1.8',
          fontSize: '1.1em',
          fontWeight: '300',
          letterSpacing: '0.02em',
        }}>
          NerdleCoin is the world's first interplanetary currency, designed to facilitate transactions between planets. It is built on the Ethereum blockchain and utilizes the ERC-20 standard.
        </p>
      </header>

      <main>
        <section style={styles.section}>
          <div style={styles.embedContainer}>
            <iframe
              height="100%"
              width="100%"
              id="geckoterminal-embed"
              title="GeckoTerminal Embed"
              src="https://www.geckoterminal.com/sepolia-testnet/pools/0x9cb9b7d1c16e3cef664c4435618f124ac2cc9c8c?embed=1&info=1&swaps=1&grayscale=0&light_chart=0"
              frameBorder="0"
              allow="clipboard-write"
              allowFullScreen
            />
          </div>
        </section>

        <div style={styles.grid}>
          <Card
            href="https://nerdle.zapier.app"
            icon="🤖"
          />
          <Card
            href="https://docs.google.com/document/d/e/2PACX-1vQ0ED6oh3-W3nAgquVbFEuCcqLrQiYSKNZVO54LlP11qUW5X9x64FejpI7EVWurQNP52AIHcr4J8jXv/pub"
            icon="📜"
          />
          <Card
            href="https://app.uniswap.org/#/swap?inputCurrency=ETH&outputCurrency=0xD9Fa3B5fd49feB9dCAd2478c8723584f342eDf62"
            icon="📊"
          />
        </div>
      </main>

      <footer style={styles.footer}>
        <nav>
          <a
            href="https://sepolia.etherscan.io/address/0xD9Fa3B5fd49feB9dCAd2478c8723584f342eDf62#code"
            style={styles.link}
          >
            Smart Contract
          </a>
          <a
            href="https://nerdlecoin.blogspot.com/"
            style={styles.link}
          >
            Blog
          </a>
          <a
            href="https://x.com/nerdlecoin"
            style={styles.link}
          >
            Twitter
          </a>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setIsChatOpen(true);
            }}
            style={styles.link}
          >
            Contact Us
          </a>
        </nav>
        <p style={styles.disclaimer}>
          Disclaimer: The information presented on this website is for
          informational purposes only and should not be construed as investment
          advice. These tokens are not being offered or sold in Texas, and this
          is neither a solicitation to buy nor an offer to sell to persons in
          Texas. Always conduct your own research and consult with a qualified
          financial advisor before making any investment decisions.
        </p>
      </footer>

      <button 
        style={styles.chatButton}
        onClick={() => setIsChatOpen(true)}
        aria-label="Open chat"
      >
        <MessageCircle color="white" size={24} />
      </button>

      <ChatPopup 
        isOpen={isChatOpen} 
        onClose={() => setIsChatOpen(false)} 
      />
    </div>
  );
};

export default App;